import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';

const AdditionalStep = ({ onNext, onBack }) => {
  const [additionalInfo, setAdditionalInfo] = useState({
    id: null,
    articles_publication: "",
    honors_distinction: "",
  });

  // Fetch additional information on mount
  useEffect(() => {
    const fetchAdditionalInfo = async () => {
      const apiUrl = "https://portal.lsb.go.ke/account/additional/";
      const accessToken = localStorage.getItem("accessToken");

      try {
        const response = await fetch(apiUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || "Failed to fetch additional information.");
        }

        const data = await response.json();
        if (data.length > 0) {
          const userData = data[0];
          setAdditionalInfo({
            id: userData.user_id,
            articles_publication: userData.articles_publication || "",
            honors_distinction: userData.honors_distinction || "",
          });
        }
      } catch (error) {
        Swal.fire({
          title: "Error",
          text: error.message,
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    };

    fetchAdditionalInfo();
  }, []);

  // Update state as user types
  const handleInputChange = (id, value) => {
    setAdditionalInfo((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  // "Step_5" is always sent as true on Next
  const handleNext = async () => {
    const apiUrl = "https://portal.lsb.go.ke/account/additional/";
    const personalApiUrl = "https://portal.lsb.go.ke/account/personal_data/";
    const accessToken = localStorage.getItem("accessToken");

    // Add additional_data_completed and step_5 to payload
    const payload = {
      id: additionalInfo.id,
      articles_publication: additionalInfo.articles_publication,
      honors_distinction: additionalInfo.honors_distinction,
      additional_data_completed: true,
      step_5: true,
    };

    try {
      const response = await fetch(apiUrl, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to update additional information.");
      }

      // --- Mark Step 5 as completed in personal_data ---
      const user_id = localStorage.getItem("user_id") || additionalInfo.id;
      if (user_id) {
        const formStep5 = new FormData();
        formStep5.append("id", user_id);
        formStep5.append("step_5", true);
        formStep5.append("additional_info_completed", true);

        const stepResponse = await fetch(personalApiUrl, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formStep5,
        });

        if (!stepResponse.ok) {
          throw new Error("Failed to mark step 5 as completed.");
        }
      }

      Swal.fire({
        title: "Success",
        text: "Additional information updated successfully!",
        icon: "success",
        confirmButtonText: "OK",
      });

      onNext();
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error.message,
        icon: "error",
        confirmButtonText: "Retry",
      });
    }
  };

  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    Swal.fire({
      title: 'Success',
      text: 'You have been logged out.',
      icon: 'success',
      width: 370,
    });
    navigate('/portal');
  };

  return (
    <div className="px-4 lg:px-8 py-4 container mx-auto">
      <div className="flex justify-between items-center pb-6">
        <h3 className="font-bold text-[#3d0707] tracking-wide underline underline-offset-4 decoration-[#ebcab3]">
          Additional Information
        </h3>

        <button
          onClick={handleLogout}
          className="py-2 px-3 bg-red-600 hover:font-bold uppercase text-white flex gap-2 items-center rounded-md text-sm transition-transform duration-200 ease-in-out hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-red-400 shadow-sm hover:shadow-lg"
        >
          <span className="transition duration-200 ease-in-out group-hover:pr-1">Logout</span>
          <LogOut className="w-5 h-5 transition-transform duration-200 group-hover:rotate-[-15deg]" />
        </button>
      </div>

      <div className="mb-6 leading-tight">
        <p className="mb-3">
          Provide any publications or distinguished honors. (Separate items with commas)
        </p>
      </div>

      {/* No manual checkbox is presented; step is considered complete on Next */}
      <form className="space-y-6" onSubmit={e => e.preventDefault()}>
        {/* Articles/ Publications */}
        <div className="bg-gray-50 border border-sky-100 rounded-xl p-4 shadow-sm flex flex-col">
          <label className="text-gray-700 font-semibold text-sm mb-2 flex gap-1" htmlFor="articles_publication">
            <span>Articles/Publications</span>
            <span className="text-[#64748b] text-xs font-normal italic">(if any)</span>
          </label>
          <textarea
            id="articles_publication"
            placeholder="E.g. Journal of Geospatial Studies, 2022; Book: Land Survey Practice, 2019"
            rows={5}
            className="rounded-md border border-sky-200 bg-white text-sm text-gray-700 shadow-sm px-3 py-2 resize-none focus:outline-sky-400 transition"
            value={additionalInfo.articles_publication}
            onChange={(e) => handleInputChange("articles_publication", e.target.value)}
          />
        </div>

        {/* Honors/ Distinctions */}
        <div className="bg-gray-50 border border-sky-100 rounded-xl p-4 shadow-sm flex flex-col">
          <label className="text-gray-700 font-semibold text-sm mb-2 flex gap-1" htmlFor="honors_distinction">
            <span>Honors/Distinctions</span>
            <span className="text-[#64748b] text-xs font-normal italic">(if any)</span>
          </label>
          <textarea
            id="honors_distinction"
            placeholder="E.g. Presidential Award, Distinguished Service Medal"
            rows={5}
            className="rounded-md border border-sky-200 bg-white text-sm text-gray-700 shadow-sm px-3 py-2 resize-none focus:outline-sky-400 transition"
            value={additionalInfo.honors_distinction}
            onChange={(e) => handleInputChange("honors_distinction", e.target.value)}
          />
        </div>
      </form>

      {/* Navigation Buttons */}
      <div className="pt-8 flex gap-3">
        <button type="button" onClick={onBack} className="button">
          <span className="button_lg">
            <span className="button_sl"></span>
            Back
          </span>
        </button>
        <button
          type="button"
          onClick={handleNext}
          className="button"
        >
          <span className="button_lg">
            <span className="button_sl"></span>
            Next
          </span>
        </button>
      </div>
    </div>
  );
};

export default AdditionalStep;